import React from 'react';

import { UxContext } from '@client/context/UxContext';
import { useGlobalStore } from '@client/store/globalStore/context/globalContext';
import i18n from '@client/utils/i18n';
import type { ProjectDoc, RecipeSection } from '@ledium/types';
import { ItemList } from '@ledium/ui-common';
import { RecipeTimeline } from '@ledium/ui-recipe-timeline';
import { Alert } from '@mui/material';

import InlineLinksSection from './Components/InlineLinksSection';

interface HasIndependentCopiesBody {
    code: 'HAS_INDEPENDENT_COPIES';
    copies: { id: string; name: string; linkedZoneId: string }[];
}

type ProjectItem = ProjectDoc & { id: string; recipeSectionList: RecipeSection[]; group?: { name: string } };

const ProjectEditorMainMenu = () => {
    const { handleAlert } = React.useContext(UxContext)!;
    const {
        globalState: { selectedGroupId }
    } = useGlobalStore();

    function isLocked(project: ProjectItem) {
        if (project.locked?.reason) {
            if (project.locked.reason === 'RUNNING_PROJECT') {
                return i18n('project_running_lock') as string;
            }
        }
        return false as const;
    }

    function filterItems(item: ProjectItem): boolean {
        if (!selectedGroupId) {
            return true;
        }
        return item.zoneId === selectedGroupId;
    }

    function groupByZone(item: ProjectItem): string {
        return `Zone: ${item.group?.name ?? 'Unknown'}`;
    }

    function nameLineExtra(project: ProjectItem) {
        return <RecipeTimeline sectionList={project.recipeSectionList} />;
    }

    function onDeleteItem(project: ProjectItem) {
        handleAlert({
            title: i18n('are_you_sure') as string,
            message: <span>{i18n('do_you_want_to_delete_X', <b>{project.name}</b>)}</span>,
            destructive: true,
            okCallback: async () => {
                const response = await fetch(`/api/projects/${project.id}`, { method: 'DELETE' });
                if (!response.ok && response.status === 400) {
                    const body = (await response.json()) as HasIndependentCopiesBody;
                    if (body.code === 'HAS_INDEPENDENT_COPIES') {
                        handleAlert({
                            title: i18n('project_has_independent_copies_title') as string,
                            message: i18n('project_has_independent_copies_message', body.copies.length) as string,
                            destructive: true,
                            okCallback: async () => {
                                await fetch(`/api/projects/${project.id}?force=true`, { method: 'DELETE' });
                            }
                        });
                    }
                }
            }
        });
    }

    function additionalItemListInfo(project: ProjectItem) {
        const count = project.linkCount ?? 0;
        if (count === 0) {
            return null;
        }
        return <InlineLinksSection zoneId={project.zoneId} linkCount={count} />;
    }

    return (
        <>
            {!selectedGroupId && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                    {i18n('zone_all_warning')}
                </Alert>
            )}
            <ItemList<ProjectItem>
                isLocked={isLocked}
                addNewDisabled={!selectedGroupId}
                filterItems={filterItems}
                nameLineExtra={nameLineExtra}
                additionalItemListInfo={additionalItemListInfo}
                groupBy={selectedGroupId ? undefined : groupByZone}
                onDeleteItem={onDeleteItem}
            />
        </>
    );
};

export default ProjectEditorMainMenu;
